import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  PencilSimple,
  Trash,
  Phone,
  EnvelopeSimple,
  User,
  Buildings,
  Shield,
  ArrowLeft,
  Warning,
  MagnifyingGlass,
  CaretRight,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (d) => {
  if (!d) return '-';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

export default function InsuranceCompanies() {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetail, setCompanyDetail] = useState(null);
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', contact_person: '', notes: ''
  });

  useEffect(() => { fetchCompanies(); }, []);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/insurance-companies`, { withCredentials: true });
      setCompanies(res.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyDetail = async (companyId) => {
    try {
      const res = await axios.get(`${API}/insurance-companies/${companyId}`, { withCredentials: true });
      setCompanyDetail(res.data);
      setSelectedCompany(companyId);
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/insurance-companies/${editing.company_id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/insurance-companies`, formData, { withCredentials: true });
      }
      toast.success(t('savedSuccess'));
      setDialogOpen(false);
      setEditing(null);
      resetForm();
      fetchCompanies();
      if (selectedCompany) fetchCompanyDetail(selectedCompany);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/insurance-companies/${deleting.company_id}`, { withCredentials: true });
      toast.success(t('deletedSuccess'));
      setDeleteDialogOpen(false);
      setDeleting(null);
      if (selectedCompany === deleting.company_id) {
        setSelectedCompany(null);
        setCompanyDetail(null);
      }
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const resetForm = () => setFormData({ name: '', phone: '', email: '', contact_person: '', notes: '' });

  const openEdit = (co) => {
    setEditing(co);
    setFormData({ name: co.name, phone: co.phone || '', email: co.email || '', contact_person: co.contact_person || '', notes: co.notes || '' });
    setDialogOpen(true);
  };

  const openAdd = () => {
    setEditing(null);
    resetForm();
    setDialogOpen(true);
  };

  const filtered = companies.filter(co =>
    co.name?.toLowerCase().includes(search.toLowerCase()) ||
    co.contact_person?.toLowerCase().includes(search.toLowerCase()) ||
    co.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status) => {
    if (status === 'active') return 'bg-emerald-50 text-emerald-700 border-emerald-300';
    if (status === 'expiring_soon') return 'bg-orange-50 text-orange-700 border-orange-300';
    return 'bg-red-50 text-red-700 border-red-300';
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="text-zinc-500">{t('loading')}</div></div></Layout>;
  }

  // Detail View
  if (selectedCompany && companyDetail) {
    return (
      <Layout>
        <div data-testid="insurance-company-detail">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="outline" size="sm" onClick={() => { setSelectedCompany(null); setCompanyDetail(null); }} className="rounded-none" data-testid="back-to-companies-btn">
              <ArrowLeft size={18} />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{companyDetail.name}</h1>
              <p className="text-sm text-zinc-500">{t('insuranceCompany')}</p>
            </div>
            <Button onClick={() => openEdit(companyDetail)} variant="outline" className="rounded-none" data-testid="edit-company-detail-btn">
              <PencilSimple size={16} className="mr-2" />{t('edit')}
            </Button>
          </div>

          {/* Company Info Card */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white border border-zinc-200 p-5 space-y-3" data-testid="company-info-card">
              <h3 className="text-sm font-semibold uppercase text-zinc-400">{t('contactInfo')}</h3>
              {companyDetail.phone && (
                <a href={`tel:${companyDetail.phone}`} className="flex items-center gap-2 text-sm hover:text-blue-600 transition-colors" data-testid="company-phone">
                  <Phone size={16} className="text-blue-600" />{companyDetail.phone}
                </a>
              )}
              {companyDetail.email && (
                <a href={`mailto:${companyDetail.email}`} className="flex items-center gap-2 text-sm hover:text-blue-600 transition-colors" data-testid="company-email">
                  <EnvelopeSimple size={16} className="text-blue-600" />{companyDetail.email}
                </a>
              )}
              {companyDetail.contact_person && (
                <div className="flex items-center gap-2 text-sm">
                  <User size={16} className="text-zinc-400" />{companyDetail.contact_person}
                </div>
              )}
              {companyDetail.notes && (
                <p className="text-sm text-zinc-500 border-t border-zinc-100 pt-3 mt-3">{companyDetail.notes}</p>
              )}
            </div>
            <div className="bg-white border border-zinc-200 p-5 text-center" data-testid="stat-total-contracts">
              <p className="text-3xl font-bold font-mono">{companyDetail.total_contracts}</p>
              <p className="text-sm text-zinc-500 mt-1">{t('totalContracts')}</p>
            </div>
            <div className="bg-white border border-zinc-200 p-5 text-center" data-testid="stat-expiring-soon">
              <p className={`text-3xl font-bold font-mono ${companyDetail.expiring_soon > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>{companyDetail.expiring_soon}</p>
              <p className="text-sm text-zinc-500 mt-1">{t('expiringSoonPolicies')}</p>
            </div>
          </div>

          {/* Buildings List */}
          <div className="bg-white border border-zinc-200" data-testid="company-buildings-list">
            <div className="px-6 py-4 border-b border-zinc-200">
              <h3 className="font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('insuredBuildings')}</h3>
            </div>
            {companyDetail.buildings.length === 0 ? (
              <div className="p-12 text-center text-zinc-400">{t('noBuildings')}</div>
            ) : (
              <table className="excel-table" data-testid="company-buildings-table">
                <thead>
                  <tr>
                    <th>{t('buildingName')}</th>
                    <th>{t('location')}</th>
                    <th>{t('policyStartDate')}</th>
                    <th>{t('policyEndDate')}</th>
                    <th>{t('status')}</th>
                    <th>{t('daysLeft')}</th>
                  </tr>
                </thead>
                <tbody>
                  {companyDetail.buildings.map((b, idx) => (
                    <tr key={idx} data-testid={`company-building-row-${idx}`}>
                      <td className="font-medium">{b.building_name}</td>
                      <td className="text-sm text-zinc-500">{b.building_location}</td>
                      <td className="font-mono text-sm">{formatDateDMY(b.policy_start_date)}</td>
                      <td className="font-mono text-sm">{formatDateDMY(b.policy_end_date)}</td>
                      <td>
                        <span className={`px-2 py-0.5 text-xs font-mono uppercase border ${getStatusBadge(b.status)}`}>
                          {t(b.status)}
                        </span>
                      </td>
                      <td className={`font-mono text-sm ${b.days_until_expiry < 0 ? 'text-red-600 font-bold' : b.days_until_expiry <= 60 ? 'text-orange-600' : 'text-emerald-600'}`}>
                        {b.days_until_expiry} {t('days')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Edit Dialog */}
        {renderFormDialog()}
      </Layout>
    );
  }

  function renderFormDialog() {
    return (
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
              {editing ? t('editInsuranceCompany') : t('addInsuranceCompany')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{t('companyName')}</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-1 rounded-none" required data-testid="company-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('phoneNumber')}</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="mt-1 rounded-none" type="tel" data-testid="company-phone-input" />
              </div>
              <div>
                <Label>{t('email')}</Label>
                <Input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-1 rounded-none" type="email" data-testid="company-email-input" />
              </div>
            </div>
            <div>
              <Label>{t('contactPerson')}</Label>
              <Input value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} className="mt-1 rounded-none" data-testid="company-contact-input" />
            </div>
            <div>
              <Label>{t('notes')}</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="mt-1 rounded-none" rows={2} data-testid="company-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
              <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-company-btn">
                <Shield size={18} className="mr-2" />{t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  // List View
  return (
    <Layout>
      <div data-testid="insurance-companies-page">
        <button
          onClick={() => navigate('/building-settings')}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4"
          data-testid="insurance-companies-back-to-settings"
        >
          <ArrowLeft size={14} />
          <span className="font-semibold">{t('settings') || 'Settings'}</span>
        </button>
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              type="text"
              placeholder={t('search') + '...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 border-zinc-200 rounded-none"
              data-testid="search-companies-input"
            />
          </div>
          <Button onClick={openAdd} className="bg-zinc-950 rounded-none" data-testid="add-company-btn">
            <Plus size={18} className="mr-2" />{t('addInsuranceCompany')}
          </Button>
        </div>

        {/* Company Table View */}
        {filtered.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-16 text-center">
            <Shield size={48} className="mx-auto text-zinc-200 mb-4" />
            <p className="text-zinc-500">{t('noInsuranceCompanies')}</p>
            <Button onClick={openAdd} className="mt-4 bg-zinc-950 rounded-none" data-testid="add-first-company-btn">
              <Plus size={16} className="mr-2" />{t('addInsuranceCompany')}
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="insurance-companies-table">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('companyName')}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('contactPerson')}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('phone')}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('email')}</th>
                    <th className="text-center px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('contracts')}</th>
                    <th className="text-center px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('expiringSoon')}</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider w-28">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filtered.map((co) => (
                    <tr
                      key={co.company_id}
                      className="hover:bg-zinc-50 transition-colors cursor-pointer"
                      onClick={() => fetchCompanyDetail(co.company_id)}
                      data-testid={`company-row-${co.company_id}`}
                    >
                      <td className="px-5 py-3 font-semibold text-zinc-900 hover:text-blue-600 transition-colors">{co.name}</td>
                      <td className="px-5 py-3 text-zinc-600">{co.contact_person || '-'}</td>
                      <td className="px-5 py-3 text-zinc-600">{co.phone || '-'}</td>
                      <td className="px-5 py-3 text-zinc-600">{co.email || '-'}</td>
                      <td className="px-5 py-3 text-center font-mono">{co.total_contracts}</td>
                      <td className="px-5 py-3 text-center">
                        {co.expiring_soon > 0 ? (
                          <span className="inline-flex items-center gap-1 text-orange-600 font-mono font-medium">
                            <Warning size={14} />{co.expiring_soon}
                          </span>
                        ) : (
                          <span className="text-emerald-600 font-mono">0</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => openEdit(co)}
                            className="p-1.5 hover:bg-zinc-100 transition-colors"
                            data-testid={`edit-company-${co.company_id}`}
                          >
                            <PencilSimple size={14} />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => { setDeleting(co); setDeleteDialogOpen(true); }}
                              className="p-1.5 hover:bg-red-50 text-red-600 transition-colors"
                              data-testid={`delete-company-${co.company_id}`}
                            >
                              <Trash size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add/Edit Dialog */}
        {renderFormDialog()}

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteInsuranceCompany')}</AlertDialogTitle>
              <AlertDialogDescription>{t('confirmDeleteCompany')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 rounded-none" data-testid="confirm-delete-company-btn">
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
